import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import {
  AuthentificationInitializerService,
  AuthentificationService,
  ServiceAuthentificationEventsEnum,
  TypeProviderUsedType,
} from '@gfc-flash/service-authentification';
import { environment } from '@env/environment';
import { EventsService } from '@gfc-flash/service-events';
import {
  DefaultInterruptSourcesConst,
  IdleService,
} from '@gfc-flash/service-idle';
import { PageLoginEventsEnum } from '@gfc-flash/page-login';
import {
  ErrorBusService,
  ErrorService,
  ServiceErrorEventsEnum,
  StoreUrlConst,
} from '@gfc-flash/service-error';
import { TypeProviderEnum } from '@gfc-flash/package-ref';
import { PageSplashscreenEventsEnum } from '@gfc-flash/page-splashscreen';
import { secondFunction } from '@gfc-flash/package-second';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Router } from '@angular/router';
import {
  LoadingController,
  ModalController,
  NavController,
  Platform,
  ToastController,
} from '@ionic/angular/standalone';
import { AppLauncher } from '@capacitor/app-launcher';
import { DeviceService } from '@gfc-flash/service-device';
import { HttpRetryService } from '@gfc-flash/service-http-retry';
import {
  ClientAppErrorStatusCodeEnum,
  ClientAuthentificationErrorStatusCodeEnum,
  ClientLegalErrorStatusCodeEnum,
  ClientNetworkErrorStatusCodeEnum,
  ClientOtherErrorStatusCodeEnum,
  HttpClientErrorStatusCodeEnum,
  HttpServerErrorStatusCodeEnum,
} from '@gfc-flash/package-logger';
import { ReplaySubject, takeUntil } from 'rxjs';
import { TranslateService } from '@gfc-flash/service-translate';
import { AlertController } from '@ionic/angular';
import { ErrorPage } from '@gfc-flash/page-error';
import { GuardAuthentificationEventsEnum } from '@gfc-flash/guard-authentification';

/**
 * @description Wires global UI, authentication, splashscreen, error and deep-link events.
 */
@Injectable({
  providedIn: 'root',
})
export class GlobalEventsInitializerService implements OnDestroy {
  private readonly platform: Platform = inject(Platform);
  private readonly authentificationService: AuthentificationService = inject(
    AuthentificationService,
  );
  private readonly authentificationInitializerService: AuthentificationInitializerService =
    inject(AuthentificationInitializerService);
  private readonly deviceService: DeviceService = inject(DeviceService);
  private readonly eventsService: EventsService = inject(EventsService);
  private readonly errorService: ErrorService = inject(ErrorService);
  private readonly errorBusService: ErrorBusService = inject(ErrorBusService);
  private readonly retryService: HttpRetryService = inject(HttpRetryService);
  private readonly idleService: IdleService = inject(IdleService);
  private readonly translateService: TranslateService =
    inject(TranslateService);
  private readonly navController: NavController = inject(NavController);
  private readonly loadingController: LoadingController =
    inject(LoadingController);
  private readonly alertController: AlertController = inject(AlertController);
  private readonly modalController: ModalController = inject(ModalController);
  private readonly toastController: ToastController = inject(ToastController);
  private readonly zone: NgZone = inject(NgZone);
  private readonly router = inject(Router);
  private destroyed$!: ReplaySubject<boolean>;
  private buffer = 0.1;
  private progress = 0;
  private idIntervalProgress?: ReturnType<typeof setInterval>;
  private idIntervalSpinner?: ReturnType<typeof setInterval>;
  private loading?: HTMLIonLoadingElement;
  private toast?: HTMLIonToastElement;
  private alert?: HTMLIonAlertElement;
  private modal?: HTMLIonModalElement;
  private authNavigationInProgress = false;
  private lastAuthTargetUrl: string | null = null;

  constructor() {}

  /**
   * @description Subscribes to global event streams and registers the Capacitor URL listener.
   */
  async initializeEvents() {
    this.destroyed$ = new ReplaySubject(1);
    this.guardAuthentificationEvents();
    this.pageLoginEvents();
    this.pageSplashscreenEvents();
    this.serviceAuthentificationEvents();
    this.serviceErrorEventsEvents();
    await this.initializeAppPlugin();
  }

  private guardAuthentificationEvents() {
    this.eventsService
      .on()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(async (event) => {
        switch (event.name as GuardAuthentificationEventsEnum) {
          case GuardAuthentificationEventsEnum.GUARD_AUTHENTIFICATION_LOADING_ACCESS_TOKEN:
            break;
          case GuardAuthentificationEventsEnum.GUARD_AUTHENTIFICATION_LOADING_APP_TOKEN:
            break;
          case GuardAuthentificationEventsEnum.GUARD_AUTHENTIFICATION_LOADING_AUTO_LOGIN:
            if (event.payload && !this.loading) {
              this.loading = await this.loadingController.create();
              await this.loading.present();
            } else {
              if (this.loading) {
                await this.loading.dismiss();
              }
            }
            break;
        }
      });
  }

  private pageLoginEvents() {
    this.eventsService
      .on()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(async (event) => {
        switch (event.name as PageLoginEventsEnum) {
          case PageLoginEventsEnum.PAGE_LOGIN_FORGOT_PASSWORD:
            break;
          case PageLoginEventsEnum.PAGE_LOGIN_LOADING:
            if (event.payload) {
              this.loading = await this.loadingController.create({});
              await this.loading.present();
            } else {
              if (this.loading) {
                await this.loading.dismiss();
              }
            }
            break;
          case PageLoginEventsEnum.PAGE_LOGIN_RESET_PASSWORD_WITH_EMAIL:
            if (event.payload) {
              this.toast = await this.toastController.create({
                message: this.translateService.instant(
                  `PAGE_LOGIN.SUCCESS.RESET_PASSWORD`,
                ),
                color: 'success',
                duration: 3000,
              });
              await this.toast.present();
            }
            break;
          case PageLoginEventsEnum.PAGE_LOGIN_RESET_PASSWORD_WITH_OTP:
            if (event.payload) {
              this.toast = await this.toastController.create({
                message: this.translateService.instant(
                  `PAGE_LOGIN.SUCCESS.RESET_PASSWORD`,
                ),
                color: 'success',
                duration: 3000,
              });
              await this.toast.present();
            }
            break;
          case PageLoginEventsEnum.PAGE_LOGIN_SIGN_IN:
            break;
          case PageLoginEventsEnum.PAGE_LOGIN_SIGN_UP:
            break;
        }
      });
  }

  private pageSplashscreenEvents() {
    this.eventsService
      .on()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(async (event) => {
        switch (event.name as PageSplashscreenEventsEnum) {
          case PageSplashscreenEventsEnum.PAGE_SPLASHSCREEN_START_PROGRESS_BAR:
            this.idIntervalProgress = setInterval(() => {
              this.eventsService.emit({
                name: PageSplashscreenEventsEnum.PAGE_SPLASHSCREEN_ON_PROGRESS,
                payload: {
                  buffer: this.progress + (this.buffer += 0.1),
                  progress: (this.progress += 0.1),
                },
              });
              if (this.buffer + this.progress > 1) {
                clearInterval(this.idIntervalProgress);
                this.buffer = 0.1;
                this.progress = 0;
                this.eventsService.emit({
                  name: PageSplashscreenEventsEnum.PAGE_SPLASHSCREEN_ON_CLOSE,
                  payload: null,
                });
              }
            }, 1000);
            break;
          case PageSplashscreenEventsEnum.PAGE_SPLASHSCREEN_START_SPINNER:
            this.idIntervalSpinner = setInterval(() => {
              this.buffer = this.progress + (this.buffer += 0.1);
              this.progress = this.progress += 0.1;
              if (this.buffer + this.progress > 1) {
                clearInterval(this.idIntervalSpinner);
                this.buffer = 0.1;
                this.progress = 0;
                this.eventsService.emit({
                  name: PageSplashscreenEventsEnum.PAGE_SPLASHSCREEN_ON_CLOSE,
                  payload: null,
                });
              }
            }, 1000);
            break;
          case PageSplashscreenEventsEnum.PAGE_SPLASHSCREEN_IS_CLOSE:
            await this.navController.navigateRoot('home');
            break;
        }
      });
  }

  private serviceAuthentificationEvents() {
    this.eventsService
      .on()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(async (event) => {
        switch (event.name as ServiceAuthentificationEventsEnum) {
          case ServiceAuthentificationEventsEnum.SERVICE_AUTHENTIFICATION_IS_AUTHENTICATE:
            if (event.payload) {
              this.idleService.setIdle(secondFunction('2m'));
              this.idleService.setTimeout(secondFunction('15s'));
              this.idleService.setInterrupts(
                DefaultInterruptSourcesConst as any,
              );
              this.idleService.watch();
            } else {
              this.idleService.stop();
            }
            break;
          case ServiceAuthentificationEventsEnum.SERVICE_AUTHENTIFICATION_AUTO_LOGIN: {
            const targetUrl = event.payload ? '/home' : '/login';
            const currentUrl = window.location.pathname;

            if (
              currentUrl === targetUrl ||
              this.lastAuthTargetUrl === targetUrl ||
              this.authNavigationInProgress
            ) {
              return;
            }

            this.lastAuthTargetUrl = targetUrl;
            this.authNavigationInProgress = true;

            try {
              await this.navController.navigateRoot(targetUrl);
            } finally {
              this.authNavigationInProgress = false;
            }

            break;
          }
          case ServiceAuthentificationEventsEnum.SERVICE_AUTHENTIFICATION_LOADING_LOGIN:
            if (event.payload) {
              this.loading = await this.loadingController.create();
              await this.loading.present();
            } else {
              if (this.loading) {
                await this.loading.dismiss();
              }
            }
            break;
          case ServiceAuthentificationEventsEnum.SERVICE_AUTHENTIFICATION_LOADING_FORGOT:
            if (event.payload) {
              this.loading = await this.loadingController.create();
              await this.loading.present();
            } else {
              if (this.loading) {
                await this.loading.dismiss();
              }
            }
            break;
          case ServiceAuthentificationEventsEnum.SERVICE_AUTHENTIFICATION_LOADING_LOGOUT:
            if (event.payload) {
              this.loading = await this.loadingController.create();
              await this.loading.present();
            } else {
              if (this.loading) {
                await this.loading.dismiss();
              }
            }
            break;
          case ServiceAuthentificationEventsEnum.SERVICE_AUTHENTIFICATION_LOADING_RESEND_OTP:
            if (event.payload) {
              this.loading = await this.loadingController.create();
              await this.loading.present();
            } else {
              if (this.loading) {
                await this.loading.dismiss();
              }
            }
            break;
          case ServiceAuthentificationEventsEnum.SERVICE_AUTHENTIFICATION_LOADING_VERIFY_OTP:
            if (event.payload) {
              this.loading = await this.loadingController.create();
              await this.loading.present();
            } else {
              if (this.loading) {
                await this.loading.dismiss();
              }
            }
            break;
          case ServiceAuthentificationEventsEnum.SERVICE_AUTHENTIFICATION_OTP_VALID:
            if (!event.payload) {
              this.alert = await this.alertController.create({
                backdropDismiss: false,
                header: this.translateService.instant(
                  `PAGE_LOGIN.ERROR.OTP_INVALID.TITLE`,
                ),
                subHeader: this.translateService.instant(
                  `PAGE_LOGIN.ERROR.OTP_INVALID.SUBTITLE`,
                ),
                message: this.translateService.instant(
                  `PAGE_LOGIN.ERROR.OTP_INVALID.MESSAGE`,
                ),
                buttons: [
                  {
                    text: this.translateService.instant(
                      'PAGE_LOGIN.BUTTONS.OK',
                    ),
                    role: 'cancel',
                  },
                ],
              });
              await this.alert.present();
            }
            break;
          case ServiceAuthentificationEventsEnum.SERVICE_AUTHENTIFICATION_OTP_RESEND:
            this.toast = await this.toastController.create({
              message: this.translateService.instant(
                `PAGE_LOGIN.SUCCESS.RESET_OTP`,
              ),
              color: 'success',
              duration: 3000,
            });
            await this.toast.present();
            break;
          case ServiceAuthentificationEventsEnum.SERVICE_AUTHENTIFICATION_LOGOUT_WITH_PASSWORD_REDIRECT:
            if (event.payload) {
              await this.navController.navigateRoot(
                event.payload.routing ?? '/login',
              );
            }
            break;
          case ServiceAuthentificationEventsEnum.SERVICE_AUTHENTIFICATION_LOGOUT_WITH_SOCIAL_REDIRECT:
            if (event.payload) {
              await this.navController.navigateRoot(
                event.payload.routing ?? '/login',
              );
            }
            break;
        }
      });
  }

  private serviceErrorEventsEvents() {
    this.eventsService
      .on()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(async (event) => {
        const errorServiceConfig = this.errorService.getErrorServiceConfig();
        switch (event.name as ServiceErrorEventsEnum) {
          case ServiceErrorEventsEnum.SERVICE_ERROR_BACK:
            this.navController.back();
            break;
          case ServiceErrorEventsEnum.SERVICE_ERROR_CLOSE:
            this.navController.back();
            break;
          case ServiceErrorEventsEnum.SERVICE_ERROR_CLOSE_APP:
            if (this.deviceService.isNativePlatform()) {
              if (!errorServiceConfig.isTesting) await App.exitApp();
            } else {
              if (!errorServiceConfig.isTesting) window.close();
            }
            break;
          case ServiceErrorEventsEnum.SERVICE_ERROR_CONTINU:
            if (!errorServiceConfig.isTesting) this.navController.back();
            break;
          case ServiceErrorEventsEnum.SERVICE_ERROR_LOGIN:
            if (event.payload.routing) {
              await this.navController.navigateRoot(event.payload.routing);
            }
            break;
          case ServiceErrorEventsEnum.SERVICE_ERROR_LOGOUT:
            this.idleService.stop();
            const typeProvider: TypeProviderUsedType<any> | null =
              await this.authentificationInitializerService.getTypeProviderUsed();
            if (typeProvider && typeProvider?.provider) {
              switch (typeProvider.provider) {
                case TypeProviderEnum.PASSWORD:
                  await this.authentificationService.logoutWithPassword({
                    loading: false,
                  });
                  break;
                case TypeProviderEnum.APPLE:
                case TypeProviderEnum.FACEBOOK:
                case TypeProviderEnum.GOOGLE:
                case TypeProviderEnum.OAUTH2:
                case TypeProviderEnum.TWITTER:
                  await this.authentificationService.logoutWithSocial({
                    socialOpts: {
                      provider: typeProvider.provider,
                      providerId: typeProvider.providerId,
                    },
                    loading: false,
                  });
                  break;
              }
            }
            if (event.payload.routing) {
              await this.navController.navigateRoot(event.payload.routing);
            }
            break;
          case ServiceErrorEventsEnum.SERVICE_ERROR_READ_CGV:
            if (event.payload.routing) {
              await this.navController.navigateRoot(event.payload.routing);
            }
            break;
          case ServiceErrorEventsEnum.SERVICE_ERROR_READ_CONSENT:
            if (event.payload.routing) {
              await this.navController.navigateRoot(event.payload.routing);
            }
            break;
          case ServiceErrorEventsEnum.SERVICE_ERROR_READ_TERMS:
            if (event.payload.routing) {
              await this.navController.navigateRoot(event.payload.routing);
            }
            break;
          case ServiceErrorEventsEnum.SERVICE_ERROR_REDIRECT:
            if (event.payload.routing) {
              await this.navController.navigateRoot(event.payload.routing);
            }
            break;
          case ServiceErrorEventsEnum.SERVICE_ERROR_RETRY:
            if (!errorServiceConfig.isTesting) this.retryService.retryRequest();
            break;
          case ServiceErrorEventsEnum.SERVICE_ERROR_STAY:
            this.navController.back();
            break;
          case ServiceErrorEventsEnum.SERVICE_ERROR_UPDATE_APP:
            const opts = errorServiceConfig.updateAppConfiguration;
            if (opts) {
              if (this.platform.is('android')) {
                if (opts.amazonStore) {
                  const url = StoreUrlConst.amazonStore.replace(
                    '%APP_ID%',
                    opts.amazonStore.appId,
                  );
                  const valueAmazonStore = await AppLauncher.canOpenUrl({
                    url,
                  });

                  if (valueAmazonStore.value) {
                    await AppLauncher.openUrl({
                      url,
                    });
                    if (!errorServiceConfig.isTesting) await App.exitApp();
                    return;
                  }
                }

                if (opts.aptoide) {
                  const url = StoreUrlConst.aptoide.replace(
                    '%APP_ID%',
                    opts.aptoide.appId,
                  );
                  const valueAptoide = await AppLauncher.canOpenUrl({
                    url,
                  });
                  if (valueAptoide.value) {
                    await AppLauncher.openUrl({
                      url,
                    });
                    if (!errorServiceConfig.isTesting) await App.exitApp();
                    return;
                  }
                }

                if (opts.fDroid) {
                  const url = StoreUrlConst.fDroid.replace(
                    '%APP_ID%',
                    opts.fDroid.appId,
                  );
                  const valueFDoid = await AppLauncher.canOpenUrl({
                    url,
                  });
                  if (valueFDoid.value) {
                    await AppLauncher.openUrl({
                      url,
                    });
                    if (!errorServiceConfig.isTesting) await App.exitApp();
                    return;
                  }
                }

                if (opts.galaxyStore) {
                  const url = StoreUrlConst.galaxyStore.replace(
                    '%APP_ID%',
                    opts.galaxyStore.appId,
                  );
                  const valueGalaxyStore = await AppLauncher.canOpenUrl({
                    url,
                  });

                  if (valueGalaxyStore.value) {
                    await AppLauncher.openUrl({
                      url,
                    });
                    if (!errorServiceConfig.isTesting) await App.exitApp();
                    return;
                  }
                }

                if (opts.huaweiAppGallery) {
                  const url = StoreUrlConst.huaweiAppGallery.replace(
                    '%APP_ID%',
                    opts.huaweiAppGallery.appId,
                  );
                  const valueHuaweiAppGallery = await AppLauncher.canOpenUrl({
                    url,
                  });

                  if (valueHuaweiAppGallery.value) {
                    await AppLauncher.openUrl({
                      url,
                    });
                    if (!errorServiceConfig.isTesting) await App.exitApp();
                    return;
                  }
                }

                if (opts.playStore) {
                  const url = StoreUrlConst.playStore.replace(
                    '%APP_ID%',
                    opts.playStore.appId,
                  );
                  const valuePlayStore = await AppLauncher.canOpenUrl({
                    url,
                  });
                  if (valuePlayStore.value) {
                    await AppLauncher.openUrl({
                      url,
                    });
                    if (!errorServiceConfig.isTesting) await App.exitApp();
                    return;
                  }
                }
              }
              if (this.platform.is('ios')) {
                if (opts.appStore) {
                  const url = StoreUrlConst.appStore
                    .replace('%APP_NAME%', opts.appStore.appName)
                    .replace('%APP_ID%', opts.appStore.appId);
                  const { value } = await AppLauncher.canOpenUrl({
                    url,
                  });
                  if (value) {
                    await AppLauncher.openUrl({
                      url,
                    });
                    if (!errorServiceConfig.isTesting) await App.exitApp();
                    return;
                  }
                }
              }
              if (this.platform.is('electron')) {
                if (opts.microsoftStore) {
                  const url = StoreUrlConst.microsoftStore.replace(
                    '%APP_ID%',
                    opts.microsoftStore.appId,
                  );
                  const { value } = await AppLauncher.canOpenUrl({
                    url,
                  });
                  if (value) {
                    await AppLauncher.openUrl({
                      url,
                    });
                    if (!errorServiceConfig.isTesting) await App.exitApp();
                    return;
                  }
                }
              }
            }
            if (!errorServiceConfig.isTesting) await App.exitApp();
            break;
        }
      });
    this.errorBusService.alertError$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(async (err) => {
        if (this.alert && this.alert.isOpen) {
          await this.alert.dismiss();
        }
        this.alert = await this.alertController.create({
          message: `(${err.error.status})`,
          subHeader: `${err.error.message}`,
          buttons: ['OK'],
          header: err.error.name,
        });
        await this.alert.present();
      });
    this.errorBusService.error$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((err) => {
        console.error(err);
      });
    this.errorBusService.loggerError$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((err) => {
        console.error(
          `${err.error.name}: ${err.error.message} (${err.error.status})`,
        );
      });
    this.errorBusService.modalError$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(async (err) => {
        switch (err.error.status) {
          case HttpServerErrorStatusCodeEnum.SERVER_NOT_FOUND:
            if (this.modal && this.modal.isOpen) {
              await this.modal.dismiss();
            }
            this.modal = await this.modalController.create({
              backdropDismiss: false,
              component: ErrorPage,
              componentProps: {
                isModal: true,
              },
            });
            break;
          case HttpClientErrorStatusCodeEnum.BAD_REQUEST:
            if (this.modal && this.modal.isOpen) {
              await this.modal.dismiss();
            }
            this.modal = await this.modalController.create({
              backdropDismiss: false,
              component: ErrorPage,
              componentProps: {
                isModal: true,
              },
            });
            break;
          case HttpClientErrorStatusCodeEnum.UNAUTHORIZED:
            if (this.modal && this.modal.isOpen) {
              await this.modal.dismiss();
            }
            this.modal = await this.modalController.create({
              backdropDismiss: false,
              component: ErrorPage,
              componentProps: {
                isModal: true,
              },
            });
            break;
          case HttpClientErrorStatusCodeEnum.NOT_FOUND:
            if (this.modal && this.modal.isOpen) {
              await this.modal.dismiss();
            }
            this.modal = await this.modalController.create({
              backdropDismiss: false,
              component: ErrorPage,
              componentProps: {
                isModal: true,
              },
            });
            break;
          case HttpServerErrorStatusCodeEnum.INTERNAL_SERVER_ERROR:
            if (this.modal && this.modal.isOpen) {
              await this.modal.dismiss();
            }
            this.modal = await this.modalController.create({
              backdropDismiss: false,
              component: ErrorPage,
              componentProps: {
                isModal: true,
              },
            });
            break;
          case HttpServerErrorStatusCodeEnum.SERVICE_UNAVAILABLE:
            if (this.modal && this.modal.isOpen) {
              await this.modal.dismiss();
            }
            this.modal = await this.modalController.create({
              backdropDismiss: false,
              component: ErrorPage,
              componentProps: {
                isModal: true,
              },
            });
            break;
          case HttpServerErrorStatusCodeEnum.GATEWAY_TIMEOUT:
            if (this.modal && this.modal.isOpen) {
              await this.modal.dismiss();
            }
            this.modal = await this.modalController.create({
              backdropDismiss: false,
              component: ErrorPage,
              componentProps: {
                isModal: true,
              },
            });
            break;
          case HttpServerErrorStatusCodeEnum.MAINTENANCE_AVAILABLE:
            if (this.modal && this.modal.isOpen) {
              await this.modal.dismiss();
            }
            this.modal = await this.modalController.create({
              backdropDismiss: false,
              component: ErrorPage,
              componentProps: {
                isModal: true,
              },
            });
            break;
          case ClientAppErrorStatusCodeEnum.APP_UPDATE_REQUIRED:
            if (this.modal && this.modal.isOpen) {
              await this.modal.dismiss();
            }
            this.modal = await this.modalController.create({
              backdropDismiss: false,
              component: ErrorPage,
              componentProps: {
                isModal: true,
              },
            });
            break;
          case ClientAuthentificationErrorStatusCodeEnum.AUTHENTIFICATION_SESSION_EXPIRED:
            if (this.modal && this.modal.isOpen) {
              await this.modal.dismiss();
            }
            this.modal = await this.modalController.create({
              backdropDismiss: false,
              component: ErrorPage,
              componentProps: {
                isModal: true,
              },
            });
            break;
          case ClientAuthentificationErrorStatusCodeEnum.AUTHENTIFICATION_SESSION_IDLE:
            if (this.modal && this.modal.isOpen) {
              await this.modal.dismiss();
            }
            this.modal = await this.modalController.create({
              backdropDismiss: false,
              component: ErrorPage,
              componentProps: {
                isModal: true,
              },
            });
            break;
          case ClientLegalErrorStatusCodeEnum.LEGAL_UPDATE_CONSENT_REQUIRE:
            if (this.modal && this.modal.isOpen) {
              await this.modal.dismiss();
            }
            this.modal = await this.modalController.create({
              backdropDismiss: false,
              component: ErrorPage,
              componentProps: {
                isModal: true,
              },
            });
            break;
          case ClientNetworkErrorStatusCodeEnum.NETWORK_TIMEOUT_ERROR:
            if (this.modal && this.modal.isOpen) {
              await this.modal.dismiss();
            }
            this.modal = await this.modalController.create({
              backdropDismiss: false,
              component: ErrorPage,
              componentProps: {
                isModal: true,
              },
            });
            break;
          case ClientOtherErrorStatusCodeEnum.OTHER_UNKNOWN_ERROR:
            if (this.modal && this.modal.isOpen) {
              await this.modal.dismiss();
            }
            this.modal = await this.modalController.create({
              backdropDismiss: false,
              component: ErrorPage,
              componentProps: {
                isModal: true,
              },
            });
            break;
          default:
            console.warn('Modal not exist');
            console.error(err);
            break;
        }
      });
    this.errorBusService.pageError$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(async (err) => {
        switch (err.error.status) {
          case HttpServerErrorStatusCodeEnum.SERVER_NOT_FOUND:
            await this.navController.navigateRoot(
              HttpServerErrorStatusCodeEnum.SERVER_NOT_FOUND.toString(10),
            );
            break;
          case HttpClientErrorStatusCodeEnum.BAD_REQUEST:
            await this.navController.navigateRoot(
              HttpClientErrorStatusCodeEnum.BAD_REQUEST.toString(10),
            );
            break;
          case HttpClientErrorStatusCodeEnum.UNAUTHORIZED:
            await this.navController.navigateRoot(
              HttpClientErrorStatusCodeEnum.UNAUTHORIZED.toString(10),
            );
            break;
          case HttpClientErrorStatusCodeEnum.NOT_FOUND:
            await this.navController.navigateRoot(
              HttpClientErrorStatusCodeEnum.NOT_FOUND.toString(10),
            );
            break;
          case HttpServerErrorStatusCodeEnum.INTERNAL_SERVER_ERROR:
            await this.navController.navigateRoot(
              HttpServerErrorStatusCodeEnum.INTERNAL_SERVER_ERROR.toString(10),
            );
            break;
          case HttpServerErrorStatusCodeEnum.SERVICE_UNAVAILABLE:
            await this.navController.navigateRoot(
              HttpServerErrorStatusCodeEnum.SERVICE_UNAVAILABLE.toString(10),
            );
            break;
          case HttpServerErrorStatusCodeEnum.GATEWAY_TIMEOUT:
            await this.navController.navigateRoot(
              HttpServerErrorStatusCodeEnum.GATEWAY_TIMEOUT.toString(10),
            );
            break;
          case HttpServerErrorStatusCodeEnum.MAINTENANCE_AVAILABLE:
            await this.navController.navigateRoot(
              HttpServerErrorStatusCodeEnum.MAINTENANCE_AVAILABLE.toString(10),
            );
            break;
          case ClientAppErrorStatusCodeEnum.APP_UPDATE_REQUIRED:
            await this.navController.navigateRoot(
              ClientAppErrorStatusCodeEnum.APP_UPDATE_REQUIRED.toString(10),
            );
            break;
          case ClientAuthentificationErrorStatusCodeEnum.AUTHENTIFICATION_SESSION_EXPIRED:
            await this.navController.navigateRoot(
              ClientAuthentificationErrorStatusCodeEnum.AUTHENTIFICATION_SESSION_EXPIRED.toString(
                10,
              ),
            );
            break;
          case ClientAuthentificationErrorStatusCodeEnum.AUTHENTIFICATION_SESSION_IDLE:
            await this.navController.navigateRoot(
              ClientAuthentificationErrorStatusCodeEnum.AUTHENTIFICATION_SESSION_IDLE.toString(
                10,
              ),
            );
            break;
          case ClientLegalErrorStatusCodeEnum.LEGAL_UPDATE_CONSENT_REQUIRE:
            await this.navController.navigateRoot(
              ClientLegalErrorStatusCodeEnum.LEGAL_UPDATE_CONSENT_REQUIRE.toString(
                10,
              ),
            );
            break;
          case ClientNetworkErrorStatusCodeEnum.NETWORK_TIMEOUT_ERROR:
            await this.navController.navigateRoot(
              ClientNetworkErrorStatusCodeEnum.NETWORK_TIMEOUT_ERROR.toString(
                10,
              ),
            );
            break;
          case ClientOtherErrorStatusCodeEnum.OTHER_UNKNOWN_ERROR:
            await this.navController.navigateRoot(
              ClientOtherErrorStatusCodeEnum.OTHER_UNKNOWN_ERROR.toString(10),
            );
            break;
          default:
            console.warn('Page not exist');
            console.error(err);
            break;
        }
      });
    this.errorBusService.toastError$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(async (err) => {
        if (this.toast && this.toast.isOpen) {
          await this.toast.dismiss();
        }
        this.toast = await this.toastController.create({
          message: `${err.error.name}: ${err.error.message} (${err.error.status})`,
          color: 'danger',
          duration: 3000,
        });
        await this.toast.present();
      });
  }

  private async initializeAppPlugin() {
    // https://capacitorjs.com/docs/guides/deep-links#ios-configuration
    // https://capacitorjs.com/docs/guides/deep-links#android-configuration
    await App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(async () => {
        // Example url: {environment.appUrl}/tabs/tab2
        // slug = /tabs/tab2
        const slug = event.url.split(environment.appUrl).pop();
        if (slug) {
          await this.router.navigateByUrl(slug);
        }
      });
    });
  }

  /**
   * @description Releases event subscriptions and Capacitor listeners owned by the initializer.
   */
  async ngOnDestroy(): Promise<void> {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    await App.removeAllListeners();
  }
}
